import { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import { getStaffsService, manageUserService } from "../../../../Services/CRMAdminServices/CRMStaffService";
import DataTable from "react-data-table-component";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { RootState } from "../../../../ReduxToolkit/Store";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";


const customStyles = {
    head: {
        style: {
            fontWeight: '800',
            color: '#0000ff',
            fontSize: '14px'
        },
    },
};

const CRMStaff = () => {
    const [staffs, setStaffs] = useState<any>([]);
    const { roles } = useSelector((state: RootState) => state.roles);
    const navigate = useSafeNavigate();
    const queryClient = useQueryClient();
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

    const { data: staffsData, isLoading, isError } = useGetRequestQuery(['getStaffs'], getStaffsService, { enabled: true });

    useEffect(() => {
        if (staffsData && !isError && !isLoading) {
            setStaffs(staffsData?.data);
            console.log("Staffs Data from CRMStaff:", staffsData?.data);
        }
    }, [staffsData, isError, isLoading]);

    const getRoleNameById = (roleID: number) => {
        const role: any = roles.find((role: any) => role.roleID === roleID);
        return role ? role.roleName : "";
    };

    const { mutate: manageUser, isPending: isManageUserPending } = useMutationRequestQuery("manageUser", manageUserService, {
        onSuccess: () => {
            toast.success("Staff deleted successfully.");
            queryClient.invalidateQueries({ queryKey: ["getStaffs"] });
            setDeleteModal(false);
        },
        onError: () => {
            toast.error("Failed to delete staff. Please reassign their leads before deleting.");
            setDeleteModal(false);
        },
    });

    const confirmDelete = (id: number) => {
        setSelectedUserId(id);
        setDeleteModal(true);
    };

    const handleDeleteClick = () => {
        if (selectedUserId) {
            manageUser({ action: 'DELETE', userID: selectedUserId });
        }
    };

    const staffColumns = [
        {
            name: "Action",
            cell: (row: any) => (
                <ul className="action simple-list flex-row">
                    <li className="edit">
                        <Link to={`/EditStaff/${row.userID}`}>
                            <i className="icon-pencil-alt" />
                        </Link>
                    </li>
                    <li className="delete">
                        <a onClick={() => confirmDelete(row.userID)} href="#">
                            <i className="icon-trash" />
                        </a>
                    </li>
                </ul>
            ),
            width: '8rem'
        },
        { name: "ID", selector: (row: any) => row["userID"], sortable: false, width: '2rem' },
        { name: "Initials", selector: (row: any) => row["initials"], sortable: false, width: '8rem' },
        { name: "Role", selector: (row: any) => getRoleNameById(row["roleID"]), sortable: false, width: '14rem' },
        { name: "First Name", selector: (row: any) => row["firstName"], sortable: false, width: '10rem' },
        { name: "Last Name", selector: (row: any) => row["lastName"], sortable: false, width: '10rem' },
        { name: "Mobile", selector: (row: any) => row["mobile"], sortable: false, width: '12rem' },
        { name: "Email", selector: (row: any) => row["email"], sortable: false, width: '18rem' },
    ];

    return (
        <div className="px-4 pt-4" style={{ minHeight: '500px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <div style={{ borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', padding: '5px', backgroundColor: '#fff' }}>
                <div className="g-3 mb-2 d-flex justify-content-between align-items-center">
                    <h3 className="f-w-900 heading-color mx-3 py-2">Staff List</h3>
                    <Button color="primary" className="mx-3" onClick={() => navigate("/AddStaff")}>Add Staff</Button>
                </div>
                {isLoading ? (
                    <div
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                        }}
                        className='mt-4 mb-2'
                    >
                        <Spinner color="primary" />
                    </div>
                ) : staffs.length !== 0 ? (
                    <DataTable
                        title=""
                        columns={staffColumns}
                        data={staffs}
                        responsive
                        customStyles={customStyles}
                    />
                ) : (
                    <div
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                        }}
                        className='mt-4 mb-2'
                    >
                        <h6>No Staff data available.</h6>
                    </div>
                )}

            </div>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Confirm Delete</ModalHeader>
                <ModalBody>Are you sure you want to delete this staff member?</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setDeleteModal(false)}>Cancel</Button>
                    <Button color="danger" onClick={handleDeleteClick} disabled={isManageUserPending}>
                        {isManageUserPending ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CRMStaff;
